import { OptionConditions, OptionConfig } from '@morph-mapper/node-logic';
import { OptionEntry, OptionType } from '@morph-mapper/types';
import { getEntries } from '@morph-mapper/utils';
import {
  EvaluationValue,
  EvaluationValueKind,
  GraphId,
  NodeEvaluation,
} from '../types';
import { err, ok, Result } from 'neverthrow';
import { GraphError } from '../../../store/graphs';

export type OptionViewModel =
  | {
      key: string;
      type: OptionType.UserDefined | OptionType.Reference | OptionType.Code | OptionType.Boolean;
      config: OptionConfig;
      value: unknown;
      visible: boolean;
    }
  | {
      key: string;
      type: OptionType.Graph;
      config: OptionConfig;
      value: EvaluationValue;
      graphId?: GraphId;
      visible: boolean;
    };

type ResolvedValues = Record<
  string,
  | {
      type: OptionType.Graph;
      value: EvaluationValue;
    }
  | {
      type: OptionType.UserDefined | OptionType.Reference | OptionType.Code | OptionType.Boolean;
      value: unknown;
    }
>;

const resolveOptionValue = (
  entry: OptionEntry<unknown> | undefined,
  config: OptionConfig
) => {
  if (!entry) return config.default;
  if ('value' in entry) return entry.value;
  return config.default;
};

const buildResolvedValues = (
  schema: Record<string, OptionConfig>,
  options: Record<string, OptionEntry<unknown>>,
  resolveGraphValue: (graphId: GraphId) => Result<NodeEvaluation, GraphError>
): Result<ResolvedValues, Error> => {
  const resolved: ResolvedValues = {};

  for (const [key, config] of getEntries(schema)) {
    const option = options[key];
    if (option?.type === OptionType.Graph) {
      const { graphId } = option;
      if (!graphId) {
        resolved[key] = {
          type: OptionType.Graph,
          value: {
            kind: EvaluationValueKind.Empty,
          },
        };
        continue;
      }
      const result = resolveGraphValue(graphId);
      if (result.isErr()) {
        return err(new Error(`Invalid graph value: ${result.error.message}`));
      }

      const { valueRef } = result.value;
      resolved[key] = { type: OptionType.Graph, value: valueRef };
      continue;
    }

    const type = (option?.type ?? OptionType.UserDefined) as
      | OptionType.UserDefined
      | OptionType.Reference
      | OptionType.Code
      | OptionType.Boolean;
    resolved[key] = {
      type,
      value: resolveOptionValue(option, config),
    };
  }

  return ok(resolved);
};

const isPreconditionValid = (
  conditions: OptionConditions | undefined,
  resolvedValues: ResolvedValues
) => {
  if (!conditions) return true;
  if (conditions.isHidden) return false;
  if (!conditions.hasValue) return true;

  return getEntries(conditions.hasValue).every(([key, expected]) => {
    if (!Object.hasOwn(resolvedValues, key)) return false;
    return resolvedValues[key].value === expected;
  });
};

export const createOptionViewModels = (
  schema: Record<string, OptionConfig>,
  options: Record<string, OptionEntry<unknown>>,
  resolveGraphValue: (graphId: GraphId) => Result<NodeEvaluation, GraphError>
): Result<OptionViewModel[], Error> => {
  const resolvedValuesResult = buildResolvedValues(
    schema,
    options,
    resolveGraphValue
  );

  if (resolvedValuesResult.isErr()) {
    return err(resolvedValuesResult.error);
  }

  const resolvedValues = resolvedValuesResult.value;
  const models: OptionViewModel[] = [];

  for (const [key, config] of getEntries(schema)) {
    const resolved = resolvedValues[key];
    const visible = isPreconditionValid(config.conditions, resolvedValues);
    const option = options[key];
    const type = option?.type ?? OptionType.UserDefined;

    if (type === OptionType.Graph) {
      if (!resolved || resolved.type !== OptionType.Graph) {
        return err(new Error(`Invalid graph value: ${resolved}`));
      }

      if (!option || option.type !== OptionType.Graph) {
        return err(new Error('Invalid graph option'));
      }

      models.push({
        key,
        type: OptionType.Graph,
        config,
        value: resolved.value,
        visible,
        graphId: option.graphId,
      });
      continue;
    }

    models.push({
      key,
      type: type as OptionType.UserDefined | OptionType.Reference | OptionType.Code | OptionType.Boolean,
      config,
      value: resolved?.value,
      visible,
    });
  }

  return ok(models);
};
